/**
 * F008: Unified Financial Tab Layout
 *
 * Complete rewrite from sidebar navigation to single scrollable page.
 * Replaces the old 8-item sidebar with:
 * - Sticky header: Entity name + Add Financial Year button
 * - Period navigator: Horizontal pills (multi-select, max 4)
 * - Toolbar: View switcher + Mode toggle + Import CSV
 * - Content area: P&L, BS, CF, EBITDA (normalised only), Ratios, Health Score
 *
 * Sprint 10, F008
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Upload, ChevronDown, ChevronRight, FileSpreadsheet, ArrowLeftRight, MoreHorizontal, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { ProfitLossPanel } from '../panels/profit-loss-panel';
import { BalanceSheetPanel } from '../panels/balance-sheet-panel';
import { CashFlowPanel } from '../panels/cashflow-panel';
import { EbitdaNormalizationPanel } from '../panels/ebitda-normalization-panel';
import { FinancialRatiosPanel } from '../panels/financial-ratios-panel';
import { HealthScorePanel } from '../panels/health-score-panel';
import { KpiSummaryCard } from '../panels/kpi-summary-card';
import { FinancialCascadePanel } from '../cascade/financial-cascade-panel';
import { AddFinancialYearWizard } from '../wizards/add-financial-year-wizard';
import { CsvImportFlow } from '../import/csv-import-flow';
import { PeriodFormModal } from '../modals/period-form-modal';
import { useUrlState } from '@/hooks/use-url-state';
import type { EntityFinancialRecord, FinancialPeriod } from '@/types/financial';

// Re-use the FinancialViewType from views if available, otherwise define locally
type ViewType = 'raw' | 'normalised' | 'insight' | 'summary';
type ModeType = 'simplified' | 'detailed';

const MAX_SELECTED_PERIODS = 4;

export interface FinancialsTabSidebarLayoutProps {
  accountId: string;
  entityRecord?: EntityFinancialRecord | null;
  periods?: FinancialPeriod[];
  isLoading?: boolean;
  isError?: boolean;
  selectedPeriod?: FinancialPeriod;
  onPeriodSelect: (period: FinancialPeriod) => void;
  onRecordCreated?: (record: EntityFinancialRecord) => void;
}

/**
 * Get trust grade colour class for period pill dot
 */
function getTrustGradeColour(grade?: string): string {
  switch (grade) {
    case 'credit_grade': return 'bg-green-500';
    case 'insight_grade': return 'bg-amber-500';
    default: return 'bg-gray-400';
  }
}

/**
 * Parse comma-separated period IDs from URL param
 */
function parsePeriodsParam(param: string): string[] {
  if (!param) return [];
  return param.split(',').filter(Boolean);
}

/**
 * Build comma-separated period IDs for URL param
 */
function buildPeriodsParam(ids: string[]): string {
  return ids.join(',');
}

function hasPeriodId<T extends { id: string }>(periods: T[], periodId?: string): periodId is string {
  return !!periodId && periods.some(period => period.id === periodId);
}

export function getDefaultSelectedPeriodId<T extends { id: string; periodStartDate: string }>(
  periods: T[],
): string | undefined {
  if (periods.length === 0) return undefined;
  const chronological = sortPeriods(periods, 'oldest-first');
  return chronological[chronological.length - 1]?.id;
}

export function resolveSelectedPeriodIds<T extends { id: string; periodStartDate: string }>(
  periodsParam: string,
  selectedPeriodId: string | undefined,
  periods: T[],
): string[] {
  const validPeriodIds = new Set(periods.map(period => period.id));
  const fromUrl = parsePeriodsParam(periodsParam).filter(periodId => validPeriodIds.has(periodId));
  if (fromUrl.length > 0) return fromUrl;
  if (hasPeriodId(periods, selectedPeriodId)) return [selectedPeriodId];
  const defaultPeriodId = getDefaultSelectedPeriodId(periods);
  return defaultPeriodId ? [defaultPeriodId] : [];
}

/**
 * Sort periods chronologically, then optionally reverse for newest-first.
 * Exported for unit testing.
 */
export function sortPeriods<T extends { periodStartDate: string }>(
  periods: T[],
  order: string,
): T[] {
  const chronological = [...periods].sort(
    (a, b) => new Date(a.periodStartDate).getTime() - new Date(b.periodStartDate).getTime(),
  );
  return order === 'oldest-first' ? chronological : [...chronological].reverse();
}

/**
 * Reorder selected period IDs to match the sort order derived from all periods.
 * This is the single source of truth for column ordering — used by chips AND panels.
 * Exported for unit testing.
 */
export function sortSelectedIds<T extends { id: string; periodStartDate: string }>(
  selectedIds: string[],
  allPeriods: T[],
  order: string,
): string[] {
  const sorted = sortPeriods(allPeriods, order);
  return sorted.map(p => p.id).filter(id => selectedIds.includes(id));
}

/**
 * Unified Financial Tab Layout
 *
 * Single scrollable page with sticky header, period navigator, and stacked panels.
 * Replaces the old sidebar navigation layout.
 */
export const FinancialsTabSidebarLayout: React.FC<FinancialsTabSidebarLayoutProps> = ({
  accountId,
  entityRecord,
  periods = [],
  isLoading = false,
  isError = false,
  selectedPeriod,
  onPeriodSelect,
  onRecordCreated,
}) => {
  // URL state for view and mode
  const [activeView, setActiveView] = useUrlState<string>('view', 'raw');
  const [activeMode, setActiveMode] = useUrlState<string>('mode', 'simplified');
  const [periodsParam, setPeriodsParam] = useUrlState<string>('periods', '');
  const [columnOrder, setColumnOrder] = useUrlState<string>('order', 'newest-first');

  // Wizard/dialog state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Period edit/delete state (F010)
  const [editingPeriod, setEditingPeriod] = useState<FinancialPeriod | null>(null);
  const [periodToDelete, setPeriodToDelete] = useState<FinancialPeriod | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Delete period mutation (F010)
  const deleteMutation = useMutation({
    mutationFn: async (periodId: string) => {
      await apiRequest('DELETE', `/api/financial/periods/${periodId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'periods', entityRecord?.id] });
      toast({
        title: 'Period deleted',
        description: 'The financial period and all associated data have been deleted.',
      });
      if (periodToDelete) {
        const remainingPeriods = periods.filter(period => period.id !== periodToDelete.id);
        const remainingSelectedIds = selectedPeriodIds.filter(id => id !== periodToDelete.id);
        const nextSelectedIds = resolveSelectedPeriodIds(
          buildPeriodsParam(remainingSelectedIds),
          undefined,
          remainingPeriods,
        );

        setPeriodsParam(buildPeriodsParam(nextSelectedIds));

        const nextSelectedPeriod = nextSelectedIds[0]
          ? remainingPeriods.find(period => period.id === nextSelectedIds[0])
          : undefined;
        if (nextSelectedPeriod) {
          onPeriodSelect(nextSelectedPeriod);
        }
      }
      setPeriodToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete period',
        variant: 'destructive',
      });
    },
  });

  // Section collapse state (P&L and BS default open, Cash Flow default closed)
  const [plOpen, setPlOpen] = useState(true);
  const [bsOpen, setBsOpen] = useState(true);
  const [cashFlowOpen, setCashFlowOpen] = useState(false);

  // Sort periods: newest-first (default) or oldest-first based on columnOrder URL param
  const sortedPeriods = useMemo(() => sortPeriods(periods, columnOrder), [periods, columnOrder]);

  // Selected period IDs from URL
  const selectedPeriodIds = useMemo(() => {
    return resolveSelectedPeriodIds(periodsParam, selectedPeriod?.id, periods);
  }, [periodsParam, selectedPeriod?.id, periods]);

  // Single source of truth: selected IDs reordered to match the current sort direction.
  // Both chips and ALL statement panels consume this array for consistent column ordering.
  const sortedSelectedPeriodIds = useMemo(
    () => sortSelectedIds(selectedPeriodIds, periods, columnOrder),
    [selectedPeriodIds, periods, columnOrder],
  );

  // Toggle a period in the selection
  const togglePeriod = useCallback((periodId: string) => {
    const current = selectedPeriodIds;
    let next: string[];

    if (current.includes(periodId)) {
      // Deselect
      next = current.filter(id => id !== periodId);
    } else {
      // Select only if under max
      if (current.length >= MAX_SELECTED_PERIODS) return;
      next = [...current, periodId];
    }

    setPeriodsParam(buildPeriodsParam(next));

    // Also notify parent of the first selected period for backwards compatibility
    if (next.length > 0) {
      const firstPeriod = periods.find(p => p.id === next[0]);
      if (firstPeriod) onPeriodSelect(firstPeriod);
    }
  }, [selectedPeriodIds, setPeriodsParam, periods, onPeriodSelect]);

  // First selected period for panels that need a single periodId (respects sort order)
  const firstSelectedPeriodId = sortedSelectedPeriodIds[0];

  // Get recordId from URL for health score panel backwards compat
  const getRecordIdFromUrl = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('recordId') || undefined;
    }
    return undefined;
  };
  const urlRecordId = getRecordIdFromUrl();

  const currentView = activeView as ViewType;
  const currentMode = activeMode as ModeType;

  // One wizard for every layout state (issue 2227). Creating the first year moves
  // the layout from an empty state to the main view; a wizard drawn inside
  // each branch would remount there and lose its step and sub-flow. The key
  // keeps it the same instance under the shared root element.
  const wizard = (
    <AddFinancialYearWizard
      key="add-financial-year-wizard"
      open={wizardOpen}
      onClose={() => setWizardOpen(false)}
      onSuccess={(periodId) => {
        setWizardOpen(false);
        setPeriodsParam(buildPeriodsParam([periodId]));
        onRecordCreated?.({} as EntityFinancialRecord);
      }}
      context="account"
      accountId={accountId}
    />
  );

  if (isError) {
    return (
      <div data-testid="unified-financial-layout">
        <p role="alert">Failed to load financial data. Refresh this page to try again.</p>
        {wizard}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="unified-financial-layout">
        <Card>
          <CardContent role="status" className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="sr-only">Loading financial data...</span>
          </CardContent>
        </Card>
        {wizard}
      </div>
    );
  }

  // === Empty States ===

  if (!entityRecord) {
    return (
      <div data-testid="unified-financial-layout">
        <div data-testid="empty-state-no-data" className="flex flex-col items-center justify-center py-16 text-center">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No financial data yet</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            Start by adding a financial year to begin tracking profit and loss, balance sheet, and other financial data.
          </p>
          <Button
            data-testid="add-financial-year-button"
            onClick={() => setWizardOpen(true)}
            size="lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            + Add Financial Year
          </Button>
        </div>
        {wizard}
      </div>
    );
  }

  if (sortedPeriods.length === 0) {
    return (
      <div data-testid="unified-financial-layout">
        <div data-testid="empty-state-no-data" className="flex flex-col items-center justify-center py-16 text-center">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No periods yet</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            Add a financial year to create your first period and start entering financial data.
          </p>
          <Button
            data-testid="add-financial-year-button"
            onClick={() => setWizardOpen(true)}
            size="lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            + Add Financial Year
          </Button>
        </div>
        {wizard}
      </div>
    );
  }

  // === Main Layout ===

  return (
    <div data-testid="unified-financial-layout" className="relative">
      {/* Sticky Header Area */}
      <div
        data-testid="financial-sticky-header"
        className="sticky top-0 z-10 bg-background pb-3 border-b border-border mb-4"
      >
        {/* Header: Entity Name + Add Year */}
        <div data-testid="financial-header" className="flex items-center justify-between py-3">
          <h2 className="text-lg font-semibold">Financial Statements</h2>
          <Button
            data-testid="add-financial-year-button"
            variant="outline"
            size="sm"
            onClick={() => setWizardOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Financial Year
          </Button>
        </div>

        {/* Period Navigator: Horizontal pill bar */}
        <div data-testid="period-navigator" className="flex items-center gap-2 flex-wrap py-2">
          {sortedPeriods.map((period) => {
            const isSelected = selectedPeriodIds.includes(period.id);
            const atMax = selectedPeriodIds.length >= MAX_SELECTED_PERIODS;
            const isDisabled = !isSelected && atMax;
            const isLocked = period.isComplete === 1;

            return (
              <div key={period.id} className="relative group flex items-center">
                <Badge
                  data-testid={`period-pill-${period.id}`}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer select-none py-1.5 pl-3 pr-7 text-sm transition-colors',
                    isSelected && 'bg-primary text-primary-foreground',
                    !isSelected && !isDisabled && 'hover:bg-muted',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !isDisabled && togglePeriod(period.id)}
                >
                  <span
                    className={cn(
                      'inline-block h-2 w-2 rounded-full mr-1.5',
                      getTrustGradeColour(period.trustGrade)
                    )}
                  />
                  {period.periodLabel}
                </Badge>

                {/* Kebab menu — positioned over the right side of the pill */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`period-pill-menu-trigger-${period.id}`}
                      className={cn(
                        'absolute right-1 top-1/2 -translate-y-1/2',
                        'h-4 w-4 p-0 flex items-center justify-center rounded',
                        'opacity-0 group-hover:opacity-100 transition-opacity',
                        'hover:bg-muted-foreground/20',
                        isSelected ? 'text-primary-foreground' : 'text-foreground'
                      )}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Period options"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      data-testid={`period-pill-menu-edit-${period.id}`}
                      disabled={isLocked}
                      aria-disabled={isLocked}
                      onClick={() => {
                        if (!isLocked) setEditingPeriod(period);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" />
                      Edit Period
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid={`period-pill-menu-delete-${period.id}`}
                      disabled={isLocked}
                      aria-disabled={isLocked}
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        if (!isLocked) setPeriodToDelete(period);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Delete Period
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>

        {/* Toolbar: View + Mode + Import */}
        <div data-testid="financial-toolbar" className="flex items-center justify-between gap-4 py-2">
          {/* View Switcher */}
          <div className="flex items-center gap-4">
            <ToggleGroup
              type="single"
              value={currentView}
              onValueChange={(value) => value && setActiveView(value)}
              data-testid="view-toggle-group"
            >
              <ToggleGroupItem value="raw" data-testid="view-toggle-raw" className="text-xs px-3">
                Raw
              </ToggleGroupItem>
              <ToggleGroupItem value="normalised" data-testid="view-toggle-normalised" className="text-xs px-3">
                Normalised
              </ToggleGroupItem>
              <ToggleGroupItem value="insight" data-testid="view-toggle-insight" className="text-xs px-3">
                Client Insight
              </ToggleGroupItem>
              <ToggleGroupItem value="summary" data-testid="view-toggle-summary" className="text-xs px-3">
                Summary
              </ToggleGroupItem>
            </ToggleGroup>

            {currentView !== 'summary' && (
              <>
                <div className="w-px h-6 bg-border" />

                {/* Mode Toggle */}
                <ToggleGroup
                  type="single"
                  value={currentMode}
                  onValueChange={(value) => value && setActiveMode(value)}
                  data-testid="mode-toggle-group"
                >
                  <ToggleGroupItem value="simplified" data-testid="mode-toggle-simplified" className="text-xs px-3">
                    Simplified
                  </ToggleGroupItem>
                  <ToggleGroupItem value="detailed" data-testid="mode-toggle-detailed" className="text-xs px-3">
                    Detailed
                  </ToggleGroupItem>
                </ToggleGroup>
              </>
            )}
          </div>

          {/* Right-side toolbar actions */}
          <div className="flex items-center gap-2">
            {/* Column Order Toggle */}
            <Button
              data-testid="column-order-toggle"
              variant="outline"
              size="sm"
              title={columnOrder === 'newest-first' ? 'Newest First' : 'Oldest First'}
              onClick={() => setColumnOrder(columnOrder === 'newest-first' ? 'oldest-first' : 'newest-first')}
            >
              <ArrowLeftRight className="h-4 w-4 mr-1" />
              {columnOrder === 'newest-first' ? 'Newest First' : 'Oldest First'}
            </Button>

            {/* Import CSV — hidden in Summary view */}
            {currentView !== 'summary' && (
              <Button
                data-testid="import-csv-button"
                variant="outline"
                size="sm"
                disabled={selectedPeriodIds.length === 0}
                onClick={() => setCsvImportOpen(true)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Import CSV
              </Button>
            )}
          </div>
        </div>
      </div>

      <FinancialCascadePanel
        entityId={entityRecord.id}
        periodId={firstSelectedPeriodId}
        view={currentView}
        showDetailRows={currentView !== 'summary'}
      />

      {/* Content Area: Scrollable stacked panels */}
      {currentView === 'summary' ? (
        <div data-testid="financial-content-area" className="space-y-6">
          {/* KPI Summary Card (Sprint 12, F001) */}
          <KpiSummaryCard periodId={firstSelectedPeriodId} />

          {/* Key Ratios */}
          <Card data-testid="ratios-section">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-medium">Key Ratios</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <FinancialRatiosPanel periodId={firstSelectedPeriodId} />
            </CardContent>
          </Card>

          {/* Health Score */}
          <Card data-testid="health-score-section">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-medium">Health Score</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <HealthScorePanel
                periodId={firstSelectedPeriodId}
                recordId={urlRecordId || entityRecord?.id}
              />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div data-testid="financial-content-area" className="space-y-6">

          {/* P&L Section (collapsible, default open) */}
          <Collapsible open={plOpen} onOpenChange={setPlOpen}>
            <Card data-testid="profit-loss-section">
              <CollapsibleTrigger asChild>
                <CardHeader
                  className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium">Profit & Loss</CardTitle>
                    {plOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-4 pb-4">
                  <ProfitLossPanel
                    periodIds={sortedSelectedPeriodIds}
                    mode={currentMode as 'simplified' | 'detailed'}
                    readOnly={currentView !== 'raw'}
                    entityRecordId={entityRecord?.id}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Balance Sheet Section (collapsible, default open) */}
          <Collapsible open={bsOpen} onOpenChange={setBsOpen}>
            <Card data-testid="balance-sheet-section">
              <CollapsibleTrigger asChild>
                <CardHeader
                  className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium">Balance Sheet</CardTitle>
                    {bsOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-4 pb-4">
                  <BalanceSheetPanel
                    periodId={firstSelectedPeriodId}
                    periods={sortedPeriods.filter(p => sortedSelectedPeriodIds.includes(p.id)).map(p => ({
                      periodId: p.id,
                      label: p.periodLabel,
                      isAnnualised: false,
                      trustGrade: (p.trustGrade as 'credit_grade' | 'insight_grade') || 'insight_grade',
                    }))}
                    mode={currentMode as 'simplified' | 'detailed'}
                    readOnly={currentView !== 'raw'}
                    entityRecordId={entityRecord?.id}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Cash Flow Section (collapsed by default) */}
          <Collapsible open={cashFlowOpen} onOpenChange={setCashFlowOpen}>
            <Card data-testid="cash-flow-section">
              <CollapsibleTrigger asChild>
                <CardHeader
                  data-testid="cash-flow-trigger"
                  className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium">Cash Flow</CardTitle>
                    {cashFlowOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-4 pb-4">
                  <CashFlowPanel periodId={firstSelectedPeriodId} />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* EBITDA Adjustments - Normalised view only */}
          {currentView === 'normalised' && (
            <Card data-testid="ebitda-adjustments-section">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base font-medium">EBITDA Adjustments</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <EbitdaNormalizationPanel periodId={firstSelectedPeriodId} />
              </CardContent>
            </Card>
          )}

          {/* Key Ratios */}
          <Card data-testid="ratios-section">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-medium">Key Ratios</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <FinancialRatiosPanel periodId={firstSelectedPeriodId} />
            </CardContent>
          </Card>

          {/* Health Score */}
          <Card data-testid="health-score-section">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-medium">Health Score</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <HealthScorePanel
                periodId={firstSelectedPeriodId}
                recordId={urlRecordId || entityRecord?.id}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Wizard Dialog: the same instance as in the empty states (issue 2227) */}
      {wizard}

      {/* CSV Import Dialog — EDG-531: shared CsvImportFlow with interactive mapping */}
      {firstSelectedPeriodId && (
        <CsvImportFlow
          accountId={accountId}
          entityFinancialRecordId={entityRecord?.id}
          periodContext={{
            mode: 'import-into-existing',
            periodId: firstSelectedPeriodId,
            periodOptions: sortedPeriods.map(p => ({ id: p.id, label: p.periodLabel || `FY ${p.periodStartDate}` })),
          }}
          defaultFinancialPeriodId={firstSelectedPeriodId}
          open={csvImportOpen}
          onOpenChange={setCsvImportOpen}
          onComplete={() => setCsvImportOpen(false)}
          onCancel={() => setCsvImportOpen(false)}
        />
      )}

      {/* Period Edit Modal (F010) */}
      {entityRecord && (
        <PeriodFormModal
          open={!!editingPeriod}
          onOpenChange={(open) => { if (!open) setEditingPeriod(null); }}
          entityRecordId={entityRecord.id}
          period={editingPeriod}
        />
      )}

      {/* Delete Period Confirmation (F010) */}
      <AlertDialog open={!!periodToDelete} onOpenChange={(open) => { if (!open) setPeriodToDelete(null); }}>
        <AlertDialogContent data-testid="delete-period-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {periodToDelete?.periodLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {periodToDelete?.periodLabel}? This will permanently remove all associated financial data (P&L, Balance Sheet, Cash Flow).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-period-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-period-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (periodToDelete) deleteMutation.mutate(periodToDelete.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
